import {
  checkInUserGQL,
  loginGQL,
  disableUserGQL,
  createShareableUrlGQL,
} from "../mutations/users";
import { ApiLegacyName } from "../../constants/apollo";
import { claimLoginRewardGQL } from "../../graphql/mutations/users";
import { enterTournamentGQL, playTournamentGQL } from "../mutations/tournament";
import {
  createHouseMatchGQL,
  createMatchGQL,
  joinFriendMatchGQL,
  playHouseMatchGQL,
  playMatchGQL,
} from "../mutations/battles";
import { createMutualFollowCodeGQL } from "../mutations/friends";
import {
  endHtmlGameSessionGQL,
  enterSoloMomentGQL,
  joinLivestreamGQL,
  playSoloMomentGQL,
  startLivestreamGQL,
} from "../mutations/gameSessions";
import {
  ICreateHouseMatchInput,
  ICreateMatchParams,
  IEnterSoloGameInput,
  IJoinFriendMatchInput,
  IJoinLivestreamInput,
  IParamsEndHtmlGameSession,
  IParamsPlayMoment,
  IParamsPlayTournament,
  IPlayHouseMatchInput,
  IReEnterSoloGameInput,
  IStartLivestreamInput,
} from "../../types/gameSessions";
import { CreateShareableUrlInput, Mutation } from "../graphql";
import apollo from "../../clients/apollo";

export interface ILoginInput {
  encryptedOidcState?: string;
  oncode?: string;
}

export interface IParamsUpdateUser {
  username?: string;
  avatar?: string;
  banner?: string;
  pushSubscription?: string;
  phoneNotVerified?: string;
  otpCode?: number;
  language?: string;
  otp?: string;
}

const apolloClient = apollo.getInstance();

export const disableUser = async (): Promise<Mutation["disableUser"]> => {
  const res = await apolloClient.mutate({
    mutation: disableUserGQL,
    variables: {},
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.deleteUserV2;
};

export const checkInUser = async (): Promise<Mutation["checkinUser"]> => {
  const queryParams = new URLSearchParams(window.location.search);
  const userAgent = JSON.stringify(window.navigator.userAgent);
  const utmMedium = JSON.stringify(queryParams.get("utm_medium"));
  const utmSource = JSON.stringify(queryParams.get("utm_source"));
  const utmCampaign = JSON.stringify(queryParams.get("utm_campaign"));
  const res = await apolloClient.mutate({
    mutation: checkInUserGQL,
    variables: {
      input: { userAgent, utmSource, utmMedium, utmCampaign },
    },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.checkinUser;
};

const prioritizeOncode = (input?: ILoginInput) => {
  if (input?.oncode && input?.encryptedOidcState) {
    delete input.encryptedOidcState;
  }
  return input;
};

export const login = async (
  loginInput?: ILoginInput,
): Promise<Mutation["login"]> => {
  const res = await apolloClient.mutate({
    mutation: loginGQL,
    variables: {
      loginInput: prioritizeOncode(loginInput),
    },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.login;
};

export const createShareableUrl = async (
  input: CreateShareableUrlInput,
): Promise<Mutation["createShareableUrl"]> => {
  const res = await apolloClient.mutate({
    context: { api: ApiLegacyName.PrimaryLegacy },
    mutation: createShareableUrlGQL,
    variables: { createShareableUrlInput: input },
  });

  return res.data.createShareableUrl.url;
};
export const claimLoginReward = async (
  day: number,
): Promise<Mutation["claimLoginReward"]> => {
  const res = await apolloClient.mutate({
    mutation: claimLoginRewardGQL,
    variables: { day },
  });

  return res.data.claimLoginReward;
};

export const playTournament = async (
  gameSessionId: string,
): Promise<Mutation["playTournament"]> => {
  const response = await apolloClient.mutate({
    mutation: playTournamentGQL,
    variables: { playTournamentInput: { gameSessionId } },
    context: { api: ApiLegacyName.Legacy },
  });

  return response.data.playTournament;
};

export const enterTournament = async (
  params: IParamsPlayTournament,
): Promise<Mutation["enterTournament"]> => {
  const response = await apolloClient.mutate({
    mutation: enterTournamentGQL,
    variables: { enterTournamentInput: params },
    context: { api: ApiLegacyName.Legacy },
  });

  return response.data.enterTournament;
};

export const playSoloMoment = async (
  playSoloMomentInput: IParamsPlayMoment,
): Promise<Mutation["playSoloMoment"]> => {
  const res = await apolloClient.mutate({
    mutation: playSoloMomentGQL,
    variables: { playSoloMomentInput },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.playSoloMoment;
};

export const endHtmlGameSession = async (
  params: IParamsEndHtmlGameSession,
): Promise<Mutation["endHtmlGameSession"]> => {
  const res = await apolloClient.mutate({
    context: { api: ApiLegacyName.PrimaryLegacy },
    mutation: endHtmlGameSessionGQL,
    variables: {
      endHtmlGameSessionInput: {
        ...params,
        failureMessage: params?.failureMessage || "",
      },
    },
  });

  return res.data.endHtmlGameSession;
};

export const enterSoloGameMoment = async (
  enterSoloMomentInput: IEnterSoloGameInput | IReEnterSoloGameInput,
): Promise<Mutation["enterSoloMoment"]> => {
  const response = await apolloClient.mutate({
    mutation: enterSoloMomentGQL,
    variables: { enterSoloMomentInput },
    context: { api: ApiLegacyName.Legacy },
  });
  return response.data.enterSoloMoment;
};

export const startLivestream = async (
  startLivestreamInput: IStartLivestreamInput,
): Promise<Mutation["startLivestream"]> => {
  const response = await apolloClient.mutate({
    context: { api: ApiLegacyName.PrimaryLegacy },
    mutation: startLivestreamGQL,
    variables: { startLivestreamInput },
  });

  return response.data.startLivestream;
};

export const joinLivestream = async (
  joinLivestreamInput: IJoinLivestreamInput,
): Promise<Mutation["joinLivestream"]> => {
  const response = await apolloClient.mutate({
    context: { api: ApiLegacyName.PrimaryLegacy },
    mutation: joinLivestreamGQL,
    variables: { joinLivestreamInput },
  });
  return response.data.joinLivestream;
};

export const createMutualFollowCode = async (): Promise<
  Mutation["createMutualFollowCode"]
> => {
  const res = await apolloClient.mutate({
    mutation: createMutualFollowCodeGQL,
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.createMutualFollowCode;
};

export const createMatch = async (
  createMatchInput: ICreateMatchParams,
): Promise<Mutation["createMatch"]> => {
  const res = await apolloClient.mutate({
    mutation: createMatchGQL,
    variables: { createMatchInput },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.createMatch;
};

export const joinFriendMatch = async (
  joinFriendMatchInput: IJoinFriendMatchInput,
): Promise<Mutation["joinFriendMatch"]> => {
  const res = await apolloClient.mutate({
    mutation: joinFriendMatchGQL,
    variables: { joinFriendMatchInput },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.joinFriendMatch;
};

export const playMatch = async (
  playFriendMatchInput: IJoinFriendMatchInput,
): Promise<Mutation["playMatch"]> => {
  const res = await apolloClient.mutate({
    mutation: playMatchGQL,
    variables: { playFriendMatchInput },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.playMatch;
};

export const createHouseMatch = async (
  createHouseMatchInput: ICreateHouseMatchInput,
): Promise<Mutation["createHouseMatch"]> => {
  const res = await apolloClient.mutate({
    mutation: createHouseMatchGQL,
    variables: { createHouseMatchInput },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.createHouseMatch;
};

export const playHouseMatch = async (
  playHouseMatchInput: IPlayHouseMatchInput,
): Promise<Mutation["playHouseMatch"]> => {
  const res = await apolloClient.mutate({
    mutation: playHouseMatchGQL,
    variables: { playHouseMatchInput },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.playHouseMatch;
};
